import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  setConnectionStatus,
  setNetworkStatus,
} from "../redux/Network/network.slice";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import type { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";
import { IlocalStorageProvider } from "../hexagon/adapters/driven/providers/localStorageProvider";

interface IUseNetworkStatus {
  analyticsController: IAnalyticsController;
  timeProvider: ITimeProvider;
  localStorageProvider: IlocalStorageProvider;
}

export const useNetworkStatus = ({
  analyticsController,
  timeProvider,
  localStorageProvider,
}: IUseNetworkStatus) => {
  const { connectionStatus } = useAppSelector((state) => ({
    connectionStatus: state.network.connectionStatus,
  }));

  const dispatch = useAppDispatch();

  const goOnline = () => {
    const email = localStorageProvider.get("userEmail");

    if (email) {
      analyticsController.identify(email);
    }

    setTimeout(() => {
      // Skip everything while the user is in the connect-device flow.
      // The flow handles network transitions gracefully on its own;
      // dispatching the Redux status change here would trigger re-renders
      // through Router → AppRouter that briefly unmount the page (showing
      // the white loader), and the reload would destroy all React state.
      const hash = window.location.hash || "";
      if (hash.includes("connect-device")) {
        return;
      }

      dispatch(setNetworkStatus({ connectionStatus: true, lastOnline: null }));
      window.location.reload();
    }, 500);
  };

  const goOffline = () => {
    const time = timeProvider.getDateTime();

    // store state here
    dispatch(
      setNetworkStatus({
        connectionStatus: false,
        lastOnline: { current_time: time.format(), timestamp: time.unix() },
      })
    );
  };

  useEffect(() => {
    dispatch(setConnectionStatus(navigator.onLine));

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [dispatch, goOffline, goOnline]);

  return connectionStatus;
};
